/*
 * /fax-directory (admin list page)
 */
import {
	Button,
	Card,
	CardContent,
	Container,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Page,
	PageHeader,
	Placeholder,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { BookOpen, Pencil, Plus, Trash2 } from "lucide-react"
import { useState } from "react"
import { trpc } from "../../trpc"
import type { FaxDirectory } from "@emr/db/client"

export const Route = createFileRoute("/fax-directory/")({
	component: FaxDirectoryPage,
})

type FormState = { name: string; faxNumber: string }
const EMPTY: FormState = { name: "", faxNumber: "" }

function FaxDirectoryPage() {
	const utils = trpc.useUtils()
	const { data: entries, isLoading } = trpc.faxDirectory.getAllByFilter.useQuery({ where: { active: true } })

	const [dialogOpen, setDialogOpen] = useState(false)
	const [editing, setEditing] = useState<FaxDirectory | null>(null)
	const [form, setForm] = useState<FormState>(EMPTY)

	const create = trpc.faxDirectory.create.useMutation({
		onSuccess: () => { utils.faxDirectory.getAllByFilter.invalidate(); closeDialog() },
		onError: (e) => toast.error(e.message),
	})
	const update = trpc.faxDirectory.update.useMutation({
		onSuccess: () => { utils.faxDirectory.getAllByFilter.invalidate(); closeDialog() },
		onError: (e) => toast.error(e.message),
	})
	const remove = trpc.faxDirectory.update.useMutation({
		onSuccess: () => { utils.faxDirectory.getAllByFilter.invalidate(); toast.success("Entry removed") },
		onError: (e) => toast.error(e.message),
	})

	function openNew() { setEditing(null); setForm(EMPTY); setDialogOpen(true) }
	function openEdit(entry: FaxDirectory) { setEditing(entry); setForm({ name: entry.name, faxNumber: entry.faxNumber }); setDialogOpen(true) }
	function closeDialog() { setDialogOpen(false); setEditing(null); setForm(EMPTY) }

	function handleSave() {
		if (!form.name.trim() || !form.faxNumber.trim()) return
		if (editing) {
			update.mutate({ id: editing.id, name: form.name.trim(), faxNumber: form.faxNumber.trim() })
		} else {
			create.mutate({ name: form.name.trim(), faxNumber: form.faxNumber.trim() })
		}
	}

	const isSaving = create.isPending || update.isPending

	return (
		<Page centerVertical={false}>
			<Container width="xl" padding="lg" gap="lg">
				<PageHeader
					title="Fax Directory"
					right={{
						Component: (
							<Button onClick={openNew}>
								<Plus className="w-4 h-4 mr-1" />
								Add Entry
							</Button>
						),
					}}
				/>

				{isLoading ? (
					<Placeholder title="Loading…" />
				) : !entries || entries.length === 0 ? (
					<Placeholder title="No fax directory entries yet" />
				) : (
					<div className="flex flex-col gap-3">
						{entries.map((entry) => (
							<Card key={entry.id}>
								<CardContent className="flex items-center justify-between py-3">
									<div className="flex items-center gap-3">
										<BookOpen className="w-4 h-4 text-muted-foreground shrink-0" />
										<div>
											<p className="font-medium">{entry.name}</p>
											<p className="text-sm text-muted-foreground">{entry.faxNumber}</p>
										</div>
									</div>
									<div className="flex items-center gap-1">
										<Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(entry)}>
											<Pencil className="w-4 h-4" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 text-destructive hover:text-destructive"
											onClick={() => remove.mutate({ id: entry.id, active: false } as any)}
										>
											<Trash2 className="w-4 h-4" />
										</Button>
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				)}
			</Container>

			<Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog() }}>
				<DialogContent className="sm:max-w-sm">
					<DialogHeader>
						<DialogTitle>{editing ? "Edit Entry" : "Add Fax Directory Entry"}</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-4 py-2">
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Name <span className="text-destructive">*</span></label>
							<Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Veterans Affairs Canada" />
						</div>
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Fax Number <span className="text-destructive">*</span></label>
							<Input value={form.faxNumber} onChange={(e) => setForm((f) => ({ ...f, faxNumber: e.target.value }))} placeholder="e.g. 1-888-555-0100" />
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={closeDialog}>Cancel</Button>
						<Button onClick={handleSave} disabled={!form.name.trim() || !form.faxNumber.trim() || isSaving}>
							{isSaving ? "Saving…" : "Save"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</Page>
	)
}
